import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import './WifiSettings.css';

const WIFI_TABS = [
  { key: 'setup', label: 'Setup' },
  { key: 'connections', label: 'Connections' },
  { key: 'access', label: 'Access Policy' },
  { key: 'designer', label: 'Portal Designer' },
  { key: 'diagnostics', label: 'Diagnostics' },
];

const PORTAL_LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'nl', label: 'Dutch' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ru', label: 'Russian' },
  { code: 'zh-cn', label: 'Chinese (Simplified)' },
  { code: 'zh-tw', label: 'Taiwanese (Traditional Chinese)' },
  { code: 'hi', label: 'Hindi (Indian)' },
  { code: 'th', label: 'Thai (Thailand)' },
  { code: 'lo', label: 'Lao (Laos)' },
];

const DEFAULT_PORTAL_TRANSLATION = Object.freeze({
  headline: 'Connect to Wi-Fi',
  body: 'Get online quickly with a simple, reliable guest login.',
  connectButtonLabel: 'Connect',
  termsSummary: 'I agree to the venue Wi-Fi terms.',
  successTitle: 'Connected',
  successBody: 'You can now use the Wi-Fi.',
});

const DEFAULT_PORTAL_TRANSLATIONS = Object.freeze({
  en: { ...DEFAULT_PORTAL_TRANSLATION },
  es: {
    ...DEFAULT_PORTAL_TRANSLATION,
    headline: 'Conéctate al Wi-Fi',
    body: 'Accede a internet rápidamente con un inicio de sesión sencillo y fiable para invitados.',
    connectButtonLabel: 'Conectar',
    termsSummary: 'Acepto los términos del Wi-Fi del establecimiento.',
    successTitle: 'Conectado',
    successBody: 'Ahora puedes usar el Wi-Fi.',
  },
  it: {
    ...DEFAULT_PORTAL_TRANSLATION,
    headline: 'Connettiti al Wi-Fi',
    body: 'Accedi rapidamente a Internet con un accesso ospite semplice e affidabile.',
    connectButtonLabel: 'Connetti',
    termsSummary: 'Accetto i termini del Wi-Fi della struttura.',
    successTitle: 'Connesso',
    successBody: 'Ora puoi utilizzare il Wi-Fi.',
  },
  vi: {
    ...DEFAULT_PORTAL_TRANSLATION,
    headline: 'Kết nối Wi-Fi',
    body: 'Truy cập internet nhanh chóng với đăng nhập khách đơn giản và đáng tin cậy.',
    connectButtonLabel: 'Kết nối',
    termsSummary: 'Tôi đồng ý với các điều khoản sử dụng Wi-Fi của địa điểm.',
    successTitle: 'Đã kết nối',
    successBody: 'Bạn có thể sử dụng Wi-Fi ngay bây giờ.',
  },
});

const DEFAULT_TERMS_FULL_TEXT =
  'By using this guest Wi-Fi, you agree to use the service lawfully, not interfere with the network or other guests, and understand that access may be limited or revoked at any time to protect the venue and its users.';
const DURATION_DAY_OPTIONS = Array.from({ length: 91 }, (_item, index) => index);
const DURATION_HOUR_OPTIONS = Array.from({ length: 24 }, (_item, index) => index);
const DURATION_MINUTE_OPTIONS = Array.from({ length: 60 }, (_item, index) => index);

const DEFAULT_PORTAL_SETTINGS = {
  enabled: false,
  templateKey: 'core_v1',
  defaultLanguage: 'en',
  supportedLanguages: ['en'],
  theme: {},
  layout: {},
  content: {
    venueName: '',
    useSiteLogo: true,
    translations: {
      en: { ...DEFAULT_PORTAL_TRANSLATION },
    },
  },
  accessPolicy: {
    authorizationMinutes: 480,
    reconnectWindowMinutes: 43200,
    nicknameMode: 'optional',
    termsRequired: true,
    termsFullText: DEFAULT_TERMS_FULL_TEXT,
    successRedirectMode: 'success_page',
    successRedirectUrl: '',
  },
  successPage: {},
};

function formatDateTime(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function minutesSince(value) {
  if (!value) return Number.POSITIVE_INFINITY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return Number.POSITIVE_INFINITY;
  return Math.max(0, (Date.now() - date.getTime()) / 60000);
}

function normalizeLanguageList(languages, defaultLanguage) {
  const seen = new Set();
  const list = [];
  (Array.isArray(languages) ? languages : []).forEach((language) => {
    const code = String(language || '').trim().toLowerCase();
    if (!code || seen.has(code)) return;
    seen.add(code);
    list.push(code);
  });
  if (!seen.has(defaultLanguage)) {
    list.unshift(defaultLanguage);
  }
  return list.length ? list : [defaultLanguage];
}

function getTranslation(settings, language) {
  return {
    ...DEFAULT_PORTAL_TRANSLATION,
    ...(DEFAULT_PORTAL_TRANSLATIONS[language] || {}),
    ...(settings?.content?.translations?.[language] || {}),
  };
}

function normalizePortalSettings(settings) {
  const defaultLanguage = String(settings?.defaultLanguage || DEFAULT_PORTAL_SETTINGS.defaultLanguage)
    .trim()
    .toLowerCase() || DEFAULT_PORTAL_SETTINGS.defaultLanguage;
  const supportedLanguages = normalizeLanguageList(settings?.supportedLanguages, defaultLanguage);
  const content = settings?.content || {};
  const translations = {};

  supportedLanguages.forEach((language) => {
    translations[language] = getTranslation(settings, language);
  });

  return {
    ...DEFAULT_PORTAL_SETTINGS,
    ...settings,
    defaultLanguage,
    supportedLanguages,
    accessPolicy: {
      ...DEFAULT_PORTAL_SETTINGS.accessPolicy,
      ...(settings?.accessPolicy || {}),
      termsFullText:
        typeof settings?.accessPolicy?.termsFullText === 'string' && settings.accessPolicy.termsFullText.trim()
          ? settings.accessPolicy.termsFullText
          : DEFAULT_TERMS_FULL_TEXT,
    },
    content: {
      ...DEFAULT_PORTAL_SETTINGS.content,
      ...content,
      venueName: typeof content.venueName === 'string' ? content.venueName : '',
      useSiteLogo: typeof content.useSiteLogo === 'boolean' ? content.useSiteLogo : true,
      translations,
    },
  };
}

function splitMinutes(totalMinutes) {
  const safeValue = Math.max(0, Number.parseInt(String(totalMinutes || 0), 10) || 0);
  const days = Math.floor(safeValue / 1440);
  const hours = Math.floor((safeValue % 1440) / 60);
  const minutes = safeValue % 60;
  return { days, hours, minutes };
}

function joinMinutes(parts) {
  const days = Math.max(0, Number.parseInt(String(parts?.days || 0), 10) || 0);
  const hours = Math.max(0, Number.parseInt(String(parts?.hours || 0), 10) || 0);
  const minutes = Math.max(0, Number.parseInt(String(parts?.minutes || 0), 10) || 0);
  return (days * 1440) + (hours * 60) + minutes;
}

function formatDurationSummary(totalMinutes) {
  const parts = splitMinutes(totalMinutes);
  const tokens = [];
  if (parts.days) tokens.push(`${parts.days}d`);
  if (parts.hours) tokens.push(`${parts.hours}h`);
  if (parts.minutes || tokens.length === 0) tokens.push(`${parts.minutes}m`);
  return tokens.join(' ');
}

function buildAbsoluteAppUrl(path) {
  const value = typeof path === 'string' ? path.trim() : '';
  if (!value) return '';
  if (value.startsWith('/')) {
    if (typeof window === 'undefined') return value;
    return new URL(value, window.location.origin).toString();
  }
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    return url.toString();
  } catch (_err) {}
  if (typeof window === 'undefined') return value;
  try {
    return new URL(value, window.location.origin).toString();
  } catch (_err) {
    return '';
  }
}

function WifiSettings({ site }) {
  const history = useHistory();
  const token = localStorage.getItem('token');
  const siteId = site?.id || null;
  const [activeTab, setActiveTab] = useState('setup');

  const [controllers, setControllers] = useState([]);
  const [loadingControllers, setLoadingControllers] = useState(false);
  const [controllersError, setControllersError] = useState('');

  const [newController, setNewController] = useState({
    name: 'Main UniFi',
    baseUrl: '',
    username: '',
    password: '',
  });
  const [savingController, setSavingController] = useState(false);

  const [mapping, setMapping] = useState(null);
  const [mappingDraft, setMappingDraft] = useState({
    controllerId: '',
    unifiSite: 'default',
    wifiSsid: '',
  });
  const [savingMapping, setSavingMapping] = useState(false);
  const [mappingError, setMappingError] = useState('');
  const [portalSettings, setPortalSettings] = useState(DEFAULT_PORTAL_SETTINGS);
  const [siteLogoUrl, setSiteLogoUrl] = useState(typeof site?.logo_url === 'string' ? site.logo_url : '');
  const [menuLandingPath, setMenuLandingPath] = useState('');
  const [loadingPortalSettings, setLoadingPortalSettings] = useState(false);
  const [portalSettingsError, setPortalSettingsError] = useState('');
  const [portalSettingsMessage, setPortalSettingsMessage] = useState('');
  const [savingAccessPolicy, setSavingAccessPolicy] = useState(false);
  const [savingDesigner, setSavingDesigner] = useState(false);
  const [designerLanguage, setDesignerLanguage] = useState(DEFAULT_PORTAL_SETTINGS.defaultLanguage);
  const [wifiSessions, setWifiSessions] = useState([]);
  const [loadingWifiSessions, setLoadingWifiSessions] = useState(false);
  const [wifiSessionsError, setWifiSessionsError] = useState('');
  const [wifiEvents, setWifiEvents] = useState([]);
  const [loadingWifiEvents, setLoadingWifiEvents] = useState(false);
  const [wifiEventsError, setWifiEventsError] = useState('');
  const [diagnosticsSearch, setDiagnosticsSearch] = useState('');
  const [connectionsFilter, setConnectionsFilter] = useState('all');
  const [connectionsSearch, setConnectionsSearch] = useState('');
  const [revokingSessionId, setRevokingSessionId] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const hasSavedWifiController = Boolean(mapping?.controllerId);
  const visibleTabs = hasSavedWifiController ? WIFI_TABS : WIFI_TABS.filter((tab) => tab.key === 'setup');

  const selectedController = useMemo(() => {
    const id = Number(mappingDraft.controllerId);
    if (!Number.isFinite(id)) return null;
    return controllers.find((c) => c.id === id) || null;
  }, [controllers, mappingDraft.controllerId]);

  const loadControllers = useCallback(async () => {
    setLoadingControllers(true);
    setControllersError('');
    try {
      const response = await fetch('/api/unifi/controllers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load controllers');
      const data = await response.json();
      setControllers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load UniFi controllers', err);
      setControllersError('Failed to load UniFi controllers');
      setControllers([]);
    } finally {
      setLoadingControllers(false);
    }
  }, [token]);

  const loadMapping = useCallback(async () => {
    if (!siteId) return;
    setMappingError('');
    try {
      const response = await fetch(`/api/unifi/sites?siteId=${encodeURIComponent(String(siteId))}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load site mapping');
      const data = await response.json();
      setMapping(data || null);
      if (data) {
        setMappingDraft({
          controllerId: String(data.controllerId || ''),
          unifiSite: data.unifiSite || 'default',
          wifiSsid: data.wifiSsid || '',
        });
      }
    } catch (err) {
      console.error('Failed to load UniFi site mapping', err);
      setMappingError('Failed to load UniFi site mapping');
      setMapping(null);
    }
  }, [siteId, token]);

  const loadPortalSettings = useCallback(async () => {
    if (!siteId) {
      setPortalSettings(DEFAULT_PORTAL_SETTINGS);
      return;
    }
    setLoadingPortalSettings(true);
    setPortalSettingsError('');
    setPortalSettingsMessage('');
    try {
      const response = await fetch(`/api/sites/${encodeURIComponent(String(siteId))}/wifi`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load Wi-Fi portal settings');
      const data = await response.json();
      const nextSettings = normalizePortalSettings(data?.portalSettings || DEFAULT_PORTAL_SETTINGS);
      setPortalSettings(nextSettings);
      setSiteLogoUrl(typeof data?.siteLogoUrl === 'string' ? data.siteLogoUrl : typeof site?.logo_url === 'string' ? site.logo_url : '');
      setMenuLandingPath(typeof data?.menuLandingPath === 'string' ? data.menuLandingPath : '');
      setDesignerLanguage((prev) => (nextSettings.supportedLanguages.includes(prev) ? prev : nextSettings.defaultLanguage));
    } catch (err) {
      console.error('Failed to load Wi-Fi portal settings', err);
      setPortalSettingsError('Failed to load access policy settings');
      setPortalSettings(DEFAULT_PORTAL_SETTINGS);
      setSiteLogoUrl(typeof site?.logo_url === 'string' ? site.logo_url : '');
      setMenuLandingPath('');
      setDesignerLanguage(DEFAULT_PORTAL_SETTINGS.defaultLanguage);
    } finally {
      setLoadingPortalSettings(false);
    }
  }, [site?.logo_url, siteId, token]);

  const loadWifiSessions = useCallback(async () => {
    if (!siteId) {
      setWifiSessions([]);
      return;
    }
    setLoadingWifiSessions(true);
    setWifiSessionsError('');
    try {
      const params = new URLSearchParams();
      params.set('siteId', String(siteId));
      params.set('limit', '200');
      const response = await fetch(`/api/audience/wifi-sessions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load Wi-Fi sessions');
      const data = await response.json();
      setWifiSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (err) {
      console.error('Failed to load Wi-Fi sessions', err);
      setWifiSessionsError('Failed to load guest connections');
      setWifiSessions([]);
    } finally {
      setLoadingWifiSessions(false);
    }
  }, [siteId, token]);

  const loadWifiEvents = useCallback(async () => {
    if (!siteId) {
      setWifiEvents([]);
      return;
    }
    setLoadingWifiEvents(true);
    setWifiEventsError('');
    try {
      const params = new URLSearchParams();
      params.set('siteId', String(siteId));
      params.set('limit', '150');
      const response = await fetch(`/api/audience/wifi-events?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load Wi-Fi diagnostics');
      const data = await response.json();
      setWifiEvents(Array.isArray(data?.events) ? data.events : []);
    } catch (err) {
      console.error('Failed to load Wi-Fi diagnostics', err);
      setWifiEventsError('Failed to load Wi-Fi diagnostics');
      setWifiEvents([]);
    } finally {
      setLoadingWifiEvents(false);
    }
  }, [siteId, token]);

  useEffect(() => {
    loadControllers();
  }, [loadControllers]);

  useEffect(() => {
    loadMapping();
  }, [loadMapping]);

  useEffect(() => {
    loadPortalSettings();
  }, [loadPortalSettings]);

  useEffect(() => {
    setSiteLogoUrl(typeof site?.logo_url === 'string' ? site.logo_url : '');
  }, [site?.logo_url]);

  useEffect(() => {
    if (activeTab === 'connections') {
      loadWifiSessions();
    }
  }, [activeTab, loadWifiSessions]);

  useEffect(() => {
    if (!hasSavedWifiController && activeTab !== 'setup') {
      setActiveTab('setup');
    }
  }, [activeTab, hasSavedWifiController]);

  useEffect(() => {
    if (activeTab === 'diagnostics') {
      loadWifiEvents();
    }
  }, [activeTab, loadWifiEvents]);

  const createController = async () => {
    if (!newController.name.trim() || !newController.baseUrl.trim() || !newController.username.trim() || !newController.password) {
      setControllersError('Please fill all controller fields');
      return;
    }
    setSavingController(true);
    setControllersError('');
    try {
      const response = await fetch('/api/unifi/controllers', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newController),
      });
      if (!response.ok) throw new Error('Failed to create controller');
      await loadControllers();
      setNewController((prev) => ({ ...prev, password: '' }));
    } catch (err) {
      console.error('Failed to create controller', err);
      setControllersError('Failed to create controller');
    } finally {
      setSavingController(false);
    }
  };

  const deleteController = async (id) => {
    const ok = window.confirm('Delete this UniFi controller? Any mappings will also be removed.');
    if (!ok) return;
    setControllersError('');
    try {
      const response = await fetch(`/api/unifi/controllers/${encodeURIComponent(String(id))}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete controller');
      await loadControllers();
      await loadMapping();
    } catch (err) {
      console.error('Failed to delete controller', err);
      setControllersError('Failed to delete controller');
    }
  };

  const saveMapping = async () => {
    if (!siteId) return;
    const controllerId = Number(mappingDraft.controllerId);
    if (!Number.isFinite(controllerId)) {
      setMappingError('Select a controller');
      return;
    }
    if (!mappingDraft.unifiSite.trim()) {
      setMappingError('UniFi site is required (often "default")');
      return;
    }

    setSavingMapping(true);
    setMappingError('');
    try {
      const response = await fetch('/api/unifi/sites', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          siteId,
          controllerId,
          unifiSite: mappingDraft.unifiSite.trim(),
          wifiSsid: mappingDraft.wifiSsid.trim() || null,
        }),
      });
      if (!response.ok) throw new Error('Failed to save mapping');
      await loadMapping();
    } catch (err) {
      console.error('Failed to save mapping', err);
      setMappingError('Failed to save mapping');
    } finally {
      setSavingMapping(false);
    }
  };

  const accessPolicy = portalSettings?.accessPolicy || DEFAULT_PORTAL_SETTINGS.accessPolicy;
  const reconnectWindowMinutes = Number(accessPolicy.reconnectWindowMinutes) || 43200;
  const authorizationDuration = splitMinutes(accessPolicy.authorizationMinutes);
  const reconnectDuration = splitMinutes(accessPolicy.reconnectWindowMinutes);
  const supportedLanguages = portalSettings?.supportedLanguages || DEFAULT_PORTAL_SETTINGS.supportedLanguages;
  const defaultLanguage = portalSettings?.defaultLanguage || DEFAULT_PORTAL_SETTINGS.defaultLanguage;
  const activeDesignerLanguage = supportedLanguages.includes(designerLanguage) ? designerLanguage : defaultLanguage;
  const designerCopy = getTranslation(portalSettings, activeDesignerLanguage);
  const resolvedSiteLogoUrl = typeof siteLogoUrl === 'string' ? siteLogoUrl.trim() : '';
  const menuLandingUrl = buildAbsoluteAppUrl(menuLandingPath);
  const usingMenuRedirect = Boolean(
    menuLandingUrl &&
      accessPolicy.successRedirectMode === 'redirect_url' &&
      String(accessPolicy.successRedirectUrl || '').trim() === menuLandingUrl
  );
  const shouldUseSiteLogo = portalSettings?.content?.useSiteLogo !== false && Boolean(resolvedSiteLogoUrl);

  useEffect(() => {
    if (!supportedLanguages.includes(designerLanguage)) {
      setDesignerLanguage(defaultLanguage);
    }
  }, [defaultLanguage, designerLanguage, supportedLanguages]);

  const sessionRows = useMemo(() => {
    return wifiSessions.map((session) => {
      const ageMinutes = minutesSince(session.lastSeen);
      const activeNow = session.status === 'authorized' && ageMinutes <= 10;
      const reconnectEligible = session.status === 'authorized' && ageMinutes <= reconnectWindowMinutes;
      const recognized = Boolean(session.visitorId || session.fingerprintId);
      const guestLabel = session.nickname || (recognized ? 'Recognized guest' : 'Guest device');
      return {
        ...session,
        activeNow,
        reconnectEligible,
        recognized,
        guestLabel,
      };
    });
  }, [wifiSessions, reconnectWindowMinutes]);

  const filteredSessions = useMemo(() => {
    const search = connectionsSearch.trim().toLowerCase();
    return sessionRows.filter((session) => {
      if (connectionsFilter === 'active' && !session.activeNow) return false;
      if (connectionsFilter === 'recent' && session.status !== 'authorized') return false;
      if (connectionsFilter === 'recognized' && !session.recognized) return false;
      if (connectionsFilter === 'revoked' && session.status !== 'revoked') return false;

      if (!search) return true;
      const haystack = [
        session.guestLabel,
        session.nickname,
        session.clientMacMasked,
        session.clientMac,
        session.visitorId,
        session.authMethod,
        session.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [connectionsFilter, connectionsSearch, sessionRows]);

  const selectedSession = useMemo(() => {
    return filteredSessions.find((session) => session.id === selectedSessionId) || sessionRows.find((session) => session.id === selectedSessionId) || null;
  }, [filteredSessions, selectedSessionId, sessionRows]);

  const filteredWifiEvents = useMemo(() => {
    const search = diagnosticsSearch.trim().toLowerCase();
    if (!search) return wifiEvents;
    return wifiEvents.filter((event) => {
      const haystack = [
        event.eventType,
        event.message,
        event.clientMacMasked,
        event.clientMac,
        event.nickname,
        event.actorSource,
        event.severity,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [diagnosticsSearch, wifiEvents]);

  const patchAccessPolicy = (patch) => {
    setPortalSettings((prev) => ({
      ...prev,
      accessPolicy: {
        ...(prev?.accessPolicy || DEFAULT_PORTAL_SETTINGS.accessPolicy),
        ...patch,
      },
    }));
    setPortalSettingsMessage('');
    setPortalSettingsError('');
  };

  const updateAccessPolicy = (key, value) => {
    patchAccessPolicy({ [key]: value });
  };

  const updateDurationField = (key, part, value) => {
    const current = splitMinutes(accessPolicy[key]);
    const nextMinutes = joinMinutes({
      ...current,
      [part]: value,
    });
    updateAccessPolicy(key, nextMinutes);
  };

  const updatePortalContent = (updater) => {
    setPortalSettings((prev) => {
      const current = normalizePortalSettings(prev);
      return normalizePortalSettings(updater(current));
    });
    setPortalSettingsMessage('');
    setPortalSettingsError('');
  };

  const updateVenueName = (value) => {
    updatePortalContent((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        venueName: value,
      },
    }));
  };

  const goToSiteDetailsLogos = () => {
    const params = new URLSearchParams();
    params.set('tab', 'Settings');
    params.set('settingsSubTab', 'Site Details');
    if (siteId) params.set('siteId', String(siteId));
    history.push(`/control-panel?${params.toString()}`);
  };

  const toggleMenuRedirect = (enabled) => {
    if (enabled) {
      patchAccessPolicy({
        successRedirectMode: 'redirect_url',
        successRedirectUrl: menuLandingUrl,
      });
      return;
    }
    if (usingMenuRedirect) {
      patchAccessPolicy({
        successRedirectMode: 'success_page',
        successRedirectUrl: '',
      });
    }
  };

  const updateDesignerLanguageField = (field, value) => {
    updatePortalContent((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        translations: {
          ...prev.content.translations,
          [activeDesignerLanguage]: {
            ...getTranslation(prev, activeDesignerLanguage),
            [field]: value,
          },
        },
      },
    }));
  };

  const updateDefaultLanguage = (language) => {
    updatePortalContent((prev) => ({
      ...prev,
      defaultLanguage: language,
      supportedLanguages: normalizeLanguageList(prev.supportedLanguages, language),
    }));
    setDesignerLanguage(language);
  };

  const toggleSupportedLanguage = (language, enabled) => {
    updatePortalContent((prev) => {
      const nextLanguages = enabled
        ? normalizeLanguageList([...prev.supportedLanguages, language], prev.defaultLanguage)
        : normalizeLanguageList(
            prev.supportedLanguages.filter((item) => item !== language),
            prev.defaultLanguage
          );
      return {
        ...prev,
        supportedLanguages: nextLanguages,
      };
    });
  };

  const saveAccessPolicy = async () => {
    if (!siteId) return;

    const authorizationMinutes = Number(accessPolicy.authorizationMinutes);
    const reconnectWindowMinutes = Number(accessPolicy.reconnectWindowMinutes);

    if (!Number.isFinite(authorizationMinutes) || authorizationMinutes <= 0) {
      setPortalSettingsError('Authorization minutes must be a positive number');
      return;
    }

    if (!Number.isFinite(reconnectWindowMinutes) || reconnectWindowMinutes <= 0) {
      setPortalSettingsError('Reconnect window must be a positive number');
      return;
    }

    setSavingAccessPolicy(true);
    setPortalSettingsError('');
    setPortalSettingsMessage('');
    try {
      const response = await fetch(`/api/sites/${encodeURIComponent(String(siteId))}/wifi`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portalSettings: {
            ...portalSettings,
            accessPolicy: {
              ...accessPolicy,
              authorizationMinutes,
              reconnectWindowMinutes,
            },
          },
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to save access policy');
      }
      setPortalSettings(data?.portalSettings || portalSettings);
      setSiteLogoUrl(typeof data?.siteLogoUrl === 'string' ? data.siteLogoUrl : resolvedSiteLogoUrl);
      setMenuLandingPath(typeof data?.menuLandingPath === 'string' ? data.menuLandingPath : menuLandingPath);
      setPortalSettingsMessage('Access policy saved. Changes are live immediately.');
    } catch (err) {
      console.error('Failed to save access policy', err);
      setPortalSettingsError(err.message || 'Failed to save access policy');
    } finally {
      setSavingAccessPolicy(false);
    }
  };

  const savePortalDesigner = async () => {
    if (!siteId) return;

    setSavingDesigner(true);
    setPortalSettingsError('');
    setPortalSettingsMessage('');
    try {
      const normalized = normalizePortalSettings(portalSettings);
      const response = await fetch(`/api/sites/${encodeURIComponent(String(siteId))}/wifi`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          portalSettings: normalized,
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to save portal designer');
      }
      const nextSettings = normalizePortalSettings(data?.portalSettings || normalized);
      setPortalSettings(nextSettings);
      setSiteLogoUrl(typeof data?.siteLogoUrl === 'string' ? data.siteLogoUrl : resolvedSiteLogoUrl);
      setMenuLandingPath(typeof data?.menuLandingPath === 'string' ? data.menuLandingPath : menuLandingPath);
      setDesignerLanguage((prev) => (nextSettings.supportedLanguages.includes(prev) ? prev : nextSettings.defaultLanguage));
      setPortalSettingsMessage('Portal designer content saved. Changes are live immediately.');
    } catch (err) {
      console.error('Failed to save portal designer', err);
      setPortalSettingsError(err.message || 'Failed to save portal designer');
    } finally {
      setSavingDesigner(false);
    }
  };

  const revokeSession = async (session) => {
    if (!session?.id) return;
    const ok = window.confirm(`Disconnect ${session.guestLabel || 'this guest'} now?`);
    if (!ok) return;

    setRevokingSessionId(session.id);
    setWifiSessionsError('');
    try {
      const params = new URLSearchParams({ siteId: String(siteId) });
      const response = await fetch(`/api/audience/wifi-sessions/${encodeURIComponent(String(session.id))}?${params.toString()}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to revoke Wi-Fi session');
      await loadWifiSessions();
    } catch (err) {
      console.error('Failed to revoke Wi-Fi session', err);
      setWifiSessionsError('Failed to disconnect guest');
    } finally {
      setRevokingSessionId(null);
    }
  };

  const renderComingSoon = (title, description, bullets) => (
    <section className="wifi-settings-section">
      <div className="wifi-settings-placeholder">
        <div className="wifi-settings-placeholder-badge">Next Step</div>
        <h4>{title}</h4>
        <p>{description}</p>
        <ul className="wifi-settings-placeholder-list">
          {bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </section>
  );

  const renderSetupTab = () => (
    <>
      <section className="wifi-settings-section">
        <div className="wifi-settings-intro">
          <div>
            <p className="wifi-settings-eyebrow">Operator Setup</p>
            <h4>Connect Suitestream.tv to UniFi for this site</h4>
            <p className="wifi-settings-help">
              Add your UniFi controller first, then tell Suitestream.tv which UniFi site this venue belongs to. The
              controller URL is not the same thing as the UniFi site slug inside that controller.
            </p>
          </div>
          <div className="wifi-settings-site-meta">
            <span>Site ID: {siteId || 'Not selected'}</span>
            <span>UniFi Site: {mapping?.unifiSite || mappingDraft.unifiSite || 'default'}</span>
          </div>
        </div>
      </section>

      <section className="wifi-settings-section">
        <div className="wifi-settings-section-header">
          <div>
            <h4>UniFi Controllers</h4>
            <p className="wifi-settings-help">Store one or more controller connections, then map this site to the right UniFi site.</p>
          </div>
          <button onClick={loadControllers} disabled={loadingControllers}>
            Refresh
          </button>
        </div>

        {controllersError && <p className="wifi-settings-error">{controllersError}</p>}

        <div className="wifi-settings-grid">
          <div className="wifi-settings-card">
            <h5>Add Controller</h5>
            <label>Name</label>
            <p className="wifi-settings-field-help">A label only for your own reference inside Suitestream.tv, such as Main UniFi or Danang Controller.</p>
            <input
              value={newController.name}
              onChange={(e) => setNewController((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Main UniFi"
            />
            <label>Base URL</label>
            <p className="wifi-settings-field-help">
              This is the public HTTPS address of your UniFi controller, for example `https://wifi.soundcheckvn.com`.
              It needs working public DNS and a valid SSL certificate that `portal-service` can trust. If you use a
              self-signed certificate, set `UNIFI_CONTROLLER_TRUST_SELF_SIGNED=true` in the env file and restart
              `portal-service`.
            </p>
            <input
              value={newController.baseUrl}
              onChange={(e) => setNewController((prev) => ({ ...prev, baseUrl: e.target.value }))}
              placeholder="https://wifi.soundcheckvn.com"
            />
            <label>Username</label>
            <p className="wifi-settings-field-help">The username Suitestream.tv should use to log in to the UniFi controller above.</p>
            <input
              value={newController.username}
              onChange={(e) => setNewController((prev) => ({ ...prev, username: e.target.value }))}
              placeholder="admin"
            />
            <label>Password</label>
            <p className="wifi-settings-field-help">The password for that same UniFi controller account.</p>
            <input
              type="password"
              value={newController.password}
              onChange={(e) => setNewController((prev) => ({ ...prev, password: e.target.value }))}
              placeholder="••••••••"
            />
            <button onClick={createController} disabled={savingController}>
              Save Controller
            </button>
            <p className="wifi-settings-help">
              If your controller uses a self-signed certificate, set `UNIFI_CONTROLLER_TRUST_SELF_SIGNED=true`
              in `secrets/.env.dev` (or `secrets/.env.prod`) and restart `portal-service`.
            </p>
          </div>

          <div className="wifi-settings-card">
            <h5>Saved Controllers</h5>
            {loadingControllers && <p>Loading…</p>}
            {!loadingControllers && controllers.length === 0 && <p>No controllers yet.</p>}
            {!loadingControllers &&
              controllers.map((c) => (
                <div key={c.id} className="wifi-settings-row">
                  <div className="wifi-settings-row-main">
                    <strong>{c.name}</strong>
                    <div className="wifi-settings-muted">{c.baseUrl}</div>
                  </div>
                  <button onClick={() => deleteController(c.id)}>Delete</button>
                </div>
              ))}
          </div>
        </div>
      </section>

      <section className="wifi-settings-section">
        <div className="wifi-settings-section-header">
          <div>
            <h4>Site Mapping</h4>
            <p className="wifi-settings-help">Map the current Suitestream.tv site to the UniFi site value used during captive authorization.</p>
          </div>
          <button onClick={loadMapping} disabled={!siteId}>
            Refresh
          </button>
        </div>

        {mappingError && <p className="wifi-settings-error">{mappingError}</p>}

        <div className="wifi-settings-card">
          <p className="wifi-settings-help">
            Map this Suitestream.tv site to the UniFi site slug used by your controller. This is what the captive portal
            uses when it authorizes or removes guests.
          </p>
          <label>Controller</label>
          <p className="wifi-settings-field-help">Choose which saved UniFi controller this venue should talk to.</p>
          <select
            value={mappingDraft.controllerId}
            onChange={(e) => setMappingDraft((prev) => ({ ...prev, controllerId: e.target.value }))}
            disabled={!siteId}
          >
            <option value="">Select…</option>
            {controllers.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
          {selectedController && <div className="wifi-settings-muted">Using: {selectedController.baseUrl}</div>}
          <label>UniFi site</label>
          <p className="wifi-settings-field-help">
            This is the UniFi site slug from the controller URL, not the full URL. Example:
            `https://wifi.soundcheckvn.com/manage/lia5606n/clients/main` means the UniFi site is `lia5606n`.
          </p>
          <input
            value={mappingDraft.unifiSite}
            onChange={(e) => setMappingDraft((prev) => ({ ...prev, unifiSite: e.target.value }))}
            placeholder="default"
            disabled={!siteId}
          />
          <label>Wi‑Fi SSID (optional)</label>
          <p className="wifi-settings-field-help">Optional label only, so you can remember which guest Wi-Fi network this mapping belongs to.</p>
          <input
            value={mappingDraft.wifiSsid}
            onChange={(e) => setMappingDraft((prev) => ({ ...prev, wifiSsid: e.target.value }))}
            placeholder="Guest WiFi"
            disabled={!siteId}
          />
          <button onClick={saveMapping} disabled={savingMapping || !siteId}>
            Save Mapping
          </button>

          {mapping && (
            <div className="wifi-settings-muted" style={{ marginTop: 12 }}>
              Current mapping: controller #{mapping.controllerId}, UniFi site `{mapping.unifiSite}`
            </div>
          )}
        </div>
      </section>
    </>
  );

  const renderAccessPolicyTab = () => (
    <>
      <section className="wifi-settings-section">
        <div className="wifi-settings-intro">
          <div>
            <p className="wifi-settings-eyebrow">Access Policy</p>
            <h4>Control how guests join and reconnect</h4>
            <p className="wifi-settings-help">
              These settings save live into the site Wi-Fi portal configuration and apply immediately to the captive portal.
            </p>
          </div>
          <div className="wifi-settings-site-meta">
            <span>Session length: {formatDurationSummary(accessPolicy.authorizationMinutes)}</span>
            <span>Remember device: {formatDurationSummary(accessPolicy.reconnectWindowMinutes)}</span>
            <span>Nickname: {accessPolicy.nicknameMode}</span>
            <span>Terms: {accessPolicy.termsRequired ? 'Required' : 'Optional'}</span>
          </div>
        </div>
      </section>

      <section className="wifi-settings-section">
        <div className="wifi-settings-card">
          <div className="wifi-settings-section-header">
            <div>
              <h4>Join Rules</h4>
              <p className="wifi-settings-help">Keep this simple: how long access lasts, how reconnect behaves, and whether nickname/terms are shown.</p>
            </div>
            <button onClick={loadPortalSettings} disabled={!siteId || loadingPortalSettings || savingAccessPolicy}>
              Refresh
            </button>
          </div>

          {portalSettingsError && <p className="wifi-settings-error">{portalSettingsError}</p>}
          {portalSettingsMessage && <p className="wifi-settings-success">{portalSettingsMessage}</p>}
          {loadingPortalSettings && <p className="wifi-settings-muted">Loading access policy…</p>}

          <div className="wifi-settings-grid">
            <div>
              <label>Session length</label>
              <p className="wifi-settings-field-help">How long a guest stays connected before they need to log in again.</p>
              <div className="wifi-settings-durationGrid">
                <label className="wifi-settings-durationField">
                  <span>Days</span>
                  <select
                    value={String(authorizationDuration.days)}
                    onChange={(e) => updateDurationField('authorizationMinutes', 'days', e.target.value)}
                    disabled={!siteId || loadingPortalSettings}
                  >
                    {DURATION_DAY_OPTIONS.map((option) => (
                      <option key={`auth-days-${option}`} value={String(option)}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="wifi-settings-durationField">
                  <span>Hours</span>
                  <select
                    value={String(authorizationDuration.hours)}
                    onChange={(e) => updateDurationField('authorizationMinutes', 'hours', e.target.value)}
                    disabled={!siteId || loadingPortalSettings}
                  >
                    {DURATION_HOUR_OPTIONS.map((option) => (
                      <option key={`auth-hours-${option}`} value={String(option)}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="wifi-settings-durationField">
                  <span>Minutes</span>
                  <select
                    value={String(authorizationDuration.minutes)}
                    onChange={(e) => updateDurationField('authorizationMinutes', 'minutes', e.target.value)}
                    disabled={!siteId || loadingPortalSettings}
                  >
                    {DURATION_MINUTE_OPTIONS.map((option) => (
                      <option key={`auth-minutes-${option}`} value={String(option)}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label>Remember this device for</label>
              <p className="wifi-settings-field-help">How long we remember a device and let it reconnect without showing the join form again.</p>
              <div className="wifi-settings-durationGrid">
                <label className="wifi-settings-durationField">
                  <span>Days</span>
                  <select
                    value={String(reconnectDuration.days)}
                    onChange={(e) => updateDurationField('reconnectWindowMinutes', 'days', e.target.value)}
                    disabled={!siteId || loadingPortalSettings}
                  >
                    {DURATION_DAY_OPTIONS.map((option) => (
                      <option key={`reconnect-days-${option}`} value={String(option)}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="wifi-settings-durationField">
                  <span>Hours</span>
                  <select
                    value={String(reconnectDuration.hours)}
                    onChange={(e) => updateDurationField('reconnectWindowMinutes', 'hours', e.target.value)}
                    disabled={!siteId || loadingPortalSettings}
                  >
                    {DURATION_HOUR_OPTIONS.map((option) => (
                      <option key={`reconnect-hours-${option}`} value={String(option)}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="wifi-settings-durationField">
                  <span>Minutes</span>
                  <select
                    value={String(reconnectDuration.minutes)}
                    onChange={(e) => updateDurationField('reconnectWindowMinutes', 'minutes', e.target.value)}
                    disabled={!siteId || loadingPortalSettings}
                  >
                    {DURATION_MINUTE_OPTIONS.map((option) => (
                      <option key={`reconnect-minutes-${option}`} value={String(option)}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label>Nickname mode</label>
              <p className="wifi-settings-field-help">Choose whether guests can skip their name, optionally add it, or must enter it to continue.</p>
              <select
                value={accessPolicy.nicknameMode || 'optional'}
                onChange={(e) => updateAccessPolicy('nicknameMode', e.target.value)}
                disabled={!siteId || loadingPortalSettings}
              >
                <option value="off">Off</option>
                <option value="optional">Optional</option>
                <option value="required">Required</option>
              </select>
            </div>

            <div>
              <label>Success redirect behavior</label>
              <p className="wifi-settings-field-help">Choose whether guests stay on the simple success screen or go straight to another page after login.</p>
              <select
                value={accessPolicy.successRedirectMode || 'success_page'}
                onChange={(e) => updateAccessPolicy('successRedirectMode', e.target.value)}
                disabled={!siteId || loadingPortalSettings}
              >
                <option value="success_page">Stay on success page</option>
                <option value="redirect_url">Redirect to URL</option>
              </select>

              <label>Redirect URL</label>
              <p className="wifi-settings-field-help">Only used when redirect mode is enabled. Leave this blank if guests should stay on the Wi-Fi success page.</p>
              <input
                value={accessPolicy.successRedirectUrl || ''}
                onChange={(e) => updateAccessPolicy('successRedirectUrl', e.target.value)}
                placeholder="https://example.com/welcome"
                disabled={!siteId || loadingPortalSettings || accessPolicy.successRedirectMode !== 'redirect_url'}
              />
              {menuLandingUrl && (
                <>
                  <label className="wifi-settings-checkbox">
                    <input
                      type="checkbox"
                      checked={usingMenuRedirect}
                      onChange={(e) => toggleMenuRedirect(e.target.checked)}
                      disabled={!siteId || loadingPortalSettings}
                    />
                    <span>Redirect to Menu page</span>
                  </label>
                  <p className="wifi-settings-field-help">Uses this site&apos;s published menu landing page as the post-login destination.</p>
                </>
              )}

              <label className="wifi-settings-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(accessPolicy.termsRequired)}
                  onChange={(e) => updateAccessPolicy('termsRequired', e.target.checked)}
                  disabled={!siteId || loadingPortalSettings}
                />
                <span>Require terms acceptance</span>
              </label>
              <p className="wifi-settings-field-help">Turn this off only if you want guests to connect without ticking a terms box.</p>
            </div>
          </div>

          <label>Full terms text</label>
          <p className="wifi-settings-field-help">This is the wording shown beside the terms checkbox on the captive portal.</p>
          <textarea
            value={accessPolicy.termsFullText || ''}
            onChange={(e) => updateAccessPolicy('termsFullText', e.target.value)}
            placeholder="Enter the Wi-Fi terms shown to guests."
            disabled={!siteId || loadingPortalSettings}
            rows={6}
          />

          <div className="wifi-settings-actions">
            <button onClick={saveAccessPolicy} disabled={!siteId || loadingPortalSettings || savingAccessPolicy}>
              {savingAccessPolicy ? 'Saving…' : 'Save Access Policy'}
            </button>
            <p className="wifi-settings-help">Save is live immediately. New guest joins and reconnects will start using these rules right away.</p>
          </div>
        </div>
      </section>
    </>
  );

  const renderConnectionsTab = () => (
    <>
      <section className="wifi-settings-section">
        <div className="wifi-settings-intro">
          <div>
            <p className="wifi-settings-eyebrow">Connections</p>
            <h4>See who is connected and remove access when needed</h4>
            <p className="wifi-settings-help">
              This is a simple operator view backed by recorded Wi-Fi sessions. `Active now` means the device was seen in roughly the last 10 minutes.
            </p>
          </div>
          <div className="wifi-settings-site-meta">
            <span>Total sessions: {sessionRows.length}</span>
            <span>Active now: {sessionRows.filter((session) => session.activeNow).length}</span>
            <span>Reconnect eligible: {sessionRows.filter((session) => session.reconnectEligible).length}</span>
          </div>
        </div>
      </section>

      <section className="wifi-settings-section">
        <div className="wifi-settings-card">
          <div className="wifi-settings-section-header">
            <div>
              <h4>Guest connections</h4>
              <p className="wifi-settings-help">Refresh this list to see recently authorized guests, search by nickname or device, and disconnect a guest immediately.</p>
            </div>
            <button onClick={loadWifiSessions} disabled={!siteId || loadingWifiSessions || revokingSessionId !== null}>
              Refresh
            </button>
          </div>

          {wifiSessionsError && <p className="wifi-settings-error">{wifiSessionsError}</p>}
          {loadingWifiSessions && <p className="wifi-settings-muted">Loading guest connections…</p>}

          <div className="wifi-settings-toolbar">
            <label className="wifi-settings-toolbar-field">
              <span>Status filter</span>
              <select value={connectionsFilter} onChange={(e) => setConnectionsFilter(e.target.value)} disabled={!siteId}>
                <option value="all">All sessions</option>
                <option value="active">Active now</option>
                <option value="recent">Authorized + recent</option>
                <option value="recognized">Recognized guests</option>
                <option value="revoked">Revoked</option>
              </select>
            </label>

            <label className="wifi-settings-toolbar-field">
              <span>Search</span>
              <input
                value={connectionsSearch}
                onChange={(e) => setConnectionsSearch(e.target.value)}
                placeholder="Nickname, MAC, visitor ID"
                disabled={!siteId}
              />
            </label>
          </div>

          {!loadingWifiSessions && filteredSessions.length === 0 && (
            <p className="wifi-settings-muted">No guest sessions matched the current filter.</p>
          )}

          {filteredSessions.length > 0 && (
            <div className="wifi-settings-tableWrap">
              <table className="wifi-settings-table">
                <thead>
                  <tr>
                    <th>Guest</th>
                    <th>Status</th>
                    <th>Last seen</th>
                    <th>Reconnect</th>
                    <th>Visitor</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSessions.map((session) => (
                    <tr key={session.id} className={selectedSessionId === session.id ? 'wifi-settings-tableRowActive' : ''}>
                      <td>
                        <button type="button" className="wifi-settings-linkButton" onClick={() => setSelectedSessionId(session.id)}>
                          {session.guestLabel}
                        </button>
                        <div className="wifi-settings-muted">{session.clientMacMasked}</div>
                      </td>
                      <td>
                        <span className={`wifi-settings-pill wifi-settings-pill-${session.status}`}>
                          {session.activeNow ? 'Active now' : session.status}
                        </span>
                        <div className="wifi-settings-muted">{session.authMethod || 'terms'}</div>
                      </td>
                      <td>
                        {formatDateTime(session.lastSeen)}
                        <div className="wifi-settings-muted">Started {formatDateTime(session.sessionStart)}</div>
                      </td>
                      <td>{session.reconnectEligible ? 'Can auto-reconnect' : 'Needs login again'}</td>
                      <td>{session.visitorId || session.fingerprintId || 'Not matched yet'}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => revokeSession(session)}
                          disabled={session.status === 'revoked' || revokingSessionId === session.id}
                        >
                          {revokingSessionId === session.id ? 'Disconnecting…' : 'Disconnect'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {selectedSession && (
        <section className="wifi-settings-section">
          <div className="wifi-settings-card">
            <div className="wifi-settings-section-header">
              <div>
                <h4>Guest details</h4>
                <p className="wifi-settings-help">A quick operator summary for the selected session.</p>
              </div>
              <button type="button" onClick={() => setSelectedSessionId(null)}>
                Close
              </button>
            </div>

            <div className="wifi-settings-detailGrid">
              <div>
                <div className="wifi-settings-detailLabel">Guest label</div>
                <div>{selectedSession.guestLabel}</div>
              </div>
              <div>
                <div className="wifi-settings-detailLabel">MAC</div>
                <div>{selectedSession.clientMacMasked}</div>
              </div>
              <div>
                <div className="wifi-settings-detailLabel">Status</div>
                <div>{selectedSession.status}</div>
              </div>
              <div>
                <div className="wifi-settings-detailLabel">Reconnect status</div>
                <div>{selectedSession.reconnectEligible ? 'Still eligible' : 'Will need to log in again'}</div>
              </div>
              <div>
                <div className="wifi-settings-detailLabel">Nickname</div>
                <div>{selectedSession.nickname || 'None'}</div>
              </div>
              <div>
                <div className="wifi-settings-detailLabel">Visitor match</div>
                <div>{selectedSession.visitorId || selectedSession.fingerprintId || 'Not matched yet'}</div>
              </div>
              <div>
                <div className="wifi-settings-detailLabel">First authorized</div>
                <div>{formatDateTime(selectedSession.sessionStart)}</div>
              </div>
              <div>
                <div className="wifi-settings-detailLabel">Last seen</div>
                <div>{formatDateTime(selectedSession.lastSeen)}</div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );

  const renderDesignerTab = () => (
    <>
      <section className="wifi-settings-section">
        <div className="wifi-settings-intro">
          <div>
            <p className="wifi-settings-eyebrow">Portal Designer</p>
            <h4>Edit the words guests see on the Wi-Fi login page</h4>
            <p className="wifi-settings-help">
              This first slice keeps things simple: text, languages, and a preview. Media, richer layout controls, and auto-translate come later.
            </p>
          </div>
          <div className="wifi-settings-site-meta">
            <span>Default language: {defaultLanguage.toUpperCase()}</span>
            <span>Languages enabled: {supportedLanguages.length}</span>
            <span>Editing: {activeDesignerLanguage.toUpperCase()}</span>
          </div>
        </div>
      </section>

      <section className="wifi-settings-section">
        <div className="wifi-settings-grid wifi-settings-designerGrid">
          <div className="wifi-settings-card">
            <div className="wifi-settings-section-header">
              <div>
                <h4>Portal content</h4>
                <p className="wifi-settings-help">Choose which languages you support, then edit the exact copy guests will read before and after connecting.</p>
              </div>
              <button onClick={loadPortalSettings} disabled={!siteId || loadingPortalSettings || savingDesigner}>
                Refresh
              </button>
            </div>

            {portalSettingsError && <p className="wifi-settings-error">{portalSettingsError}</p>}
            {portalSettingsMessage && <p className="wifi-settings-success">{portalSettingsMessage}</p>}
            {loadingPortalSettings && <p className="wifi-settings-muted">Loading portal content…</p>}

            <label>Default language</label>
            <p className="wifi-settings-field-help">This is the language new guests see first. It is always kept enabled.</p>
            <select
              value={defaultLanguage}
              onChange={(e) => updateDefaultLanguage(e.target.value)}
              disabled={!siteId || loadingPortalSettings}
            >
              {PORTAL_LANGUAGE_OPTIONS.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.label}
                </option>
              ))}
            </select>

            <label>Enabled languages</label>
            <p className="wifi-settings-field-help">Tick every language you want to maintain manually in v1. Guests can only switch between enabled languages.</p>
            <div className="wifi-settings-languageList">
              {PORTAL_LANGUAGE_OPTIONS.map((language) => {
                const isDefault = language.code === defaultLanguage;
                const checked = supportedLanguages.includes(language.code);
                return (
                  <label key={language.code} className="wifi-settings-languageOption">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleSupportedLanguage(language.code, e.target.checked)}
                      disabled={!siteId || loadingPortalSettings || isDefault}
                    />
                    <span>{language.label}</span>
                    {isDefault && <small>Default</small>}
                  </label>
                );
              })}
            </div>

            <label>Editing language</label>
            <p className="wifi-settings-field-help">Switch between enabled languages to edit each version separately.</p>
            <select
              value={activeDesignerLanguage}
              onChange={(e) => setDesignerLanguage(e.target.value)}
              disabled={!siteId || loadingPortalSettings}
            >
              {supportedLanguages.map((languageCode) => {
                const label = PORTAL_LANGUAGE_OPTIONS.find((language) => language.code === languageCode)?.label || languageCode.toUpperCase();
                return (
                  <option key={languageCode} value={languageCode}>
                    {label}
                  </option>
                );
              })}
            </select>

            <label>Venue name</label>
            <p className="wifi-settings-field-help">Shown as the place name on the captive portal.</p>
            <input
              value={portalSettings?.content?.venueName || ''}
              onChange={(e) => updateVenueName(e.target.value)}
              placeholder="Suitestream.tv Guest Wi-Fi"
              disabled={!siteId || loadingPortalSettings}
            />

            {resolvedSiteLogoUrl ? (
              <>
                <label className="wifi-settings-checkbox">
                  <input
                    type="checkbox"
                    checked={portalSettings?.content?.useSiteLogo !== false}
                    onChange={(e) =>
                      updatePortalContent((prev) => ({
                        ...prev,
                        content: {
                          ...prev.content,
                          useSiteLogo: e.target.checked,
                        },
                      }))
                    }
                    disabled={!siteId || loadingPortalSettings}
                  />
                  <span>Use Logo</span>
                </label>
                <p className="wifi-settings-field-help">Uses the main logo from Settings → Site Details on the live portal and preview.</p>
              </>
            ) : (
              <div className="wifi-settings-inlineAction">
                <div>
                  <label>Use Logo</label>
                  <p className="wifi-settings-field-help">No site logo is available yet. Add one in Site Details, then come back here to use it on the portal.</p>
                </div>
                <button type="button" onClick={goToSiteDetailsLogos}>
                  Manage Logos
                </button>
              </div>
            )}

            <label>Headline</label>
            <p className="wifi-settings-field-help">The main message at the top of the login page.</p>
            <input
              value={designerCopy.headline || ''}
              onChange={(e) => updateDesignerLanguageField('headline', e.target.value)}
              placeholder="Connect to Wi-Fi"
              disabled={!siteId || loadingPortalSettings}
            />

            <label>Body text</label>
            <p className="wifi-settings-field-help">A short explanation telling guests what this page is for.</p>
            <textarea
              value={designerCopy.body || ''}
              onChange={(e) => updateDesignerLanguageField('body', e.target.value)}
              placeholder="Get online quickly with a simple, reliable guest login."
              rows={4}
              disabled={!siteId || loadingPortalSettings}
            />

            <label>Connect button label</label>
            <p className="wifi-settings-field-help">The wording on the button guests press to continue.</p>
            <input
              value={designerCopy.connectButtonLabel || ''}
              onChange={(e) => updateDesignerLanguageField('connectButtonLabel', e.target.value)}
              placeholder="Connect"
              disabled={!siteId || loadingPortalSettings}
            />

            <label>Terms summary</label>
            <p className="wifi-settings-field-help">A short version of your terms wording shown beside the acceptance checkbox.</p>
            <input
              value={designerCopy.termsSummary || ''}
              onChange={(e) => updateDesignerLanguageField('termsSummary', e.target.value)}
              placeholder="I agree to the venue Wi-Fi terms."
              disabled={!siteId || loadingPortalSettings}
            />

            <label>Success title</label>
            <p className="wifi-settings-field-help">The first line guests see after they are connected.</p>
            <input
              value={designerCopy.successTitle || ''}
              onChange={(e) => updateDesignerLanguageField('successTitle', e.target.value)}
              placeholder="Connected"
              disabled={!siteId || loadingPortalSettings}
            />

            <label>Success body</label>
            <p className="wifi-settings-field-help">A short message confirming the guest is online.</p>
            <textarea
              value={designerCopy.successBody || ''}
              onChange={(e) => updateDesignerLanguageField('successBody', e.target.value)}
              placeholder="You can now use the Wi-Fi."
              rows={3}
              disabled={!siteId || loadingPortalSettings}
            />

            <div className="wifi-settings-actions">
              <button onClick={savePortalDesigner} disabled={!siteId || loadingPortalSettings || savingDesigner}>
                {savingDesigner ? 'Saving…' : 'Save Portal Content'}
              </button>
              <p className="wifi-settings-help">Save goes live immediately. The live captive portal and this preview both use these content settings.</p>
            </div>
          </div>

          <div className="wifi-settings-card">
            <div className="wifi-settings-section-header">
              <div>
                <h4>Preview</h4>
                <p className="wifi-settings-help">A simple operator preview of the login and success copy for the language you are editing.</p>
              </div>
            </div>

            <div className="wifi-settings-preview">
              <div className="wifi-settings-previewPhone">
                <div className="wifi-settings-previewScreen">
                  <div className="wifi-settings-previewLanguage">{activeDesignerLanguage.toUpperCase()}</div>
                  {shouldUseSiteLogo && (
                    <img className="wifi-settings-previewLogo" src={resolvedSiteLogoUrl} alt="Site logo" />
                  )}
                  {(portalSettings?.content?.venueName || '').trim() && (
                    <div className="wifi-settings-previewVenue">{portalSettings.content.venueName}</div>
                  )}
                  <h5>{designerCopy.headline}</h5>
                  <p>{designerCopy.body}</p>
                  <div className="wifi-settings-previewTerms">{designerCopy.termsSummary}</div>
                  <button type="button" disabled>
                    {designerCopy.connectButtonLabel}
                  </button>
                </div>

                <div className="wifi-settings-previewSuccess">
                  <div className="wifi-settings-previewSuccessBadge">After login</div>
                  <strong>{designerCopy.successTitle}</strong>
                  <p>{designerCopy.successBody}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  const renderDiagnosticsTab = () => {
    const portalRoute = mapping?.unifiSite ? `/guest/s/${mapping.unifiSite}` : '';
    const latestEvent = wifiEvents[0] || null;

    return (
      <>
        <section className="wifi-settings-section">
          <div className="wifi-settings-intro">
            <div>
              <p className="wifi-settings-eyebrow">Diagnostics</p>
              <h4>Watch live Wi-Fi activity while you test</h4>
              <p className="wifi-settings-help">
                This view tails recent captive portal events and operator disconnect actions. Keep it open while testing joins, reconnects, and revokes.
              </p>
            </div>
            <div className="wifi-settings-site-meta">
              <span>Events loaded: {wifiEvents.length}</span>
              <span>Refresh mode: Manual</span>
              <span>Latest event: {latestEvent ? formatDateTime(latestEvent.createdAt) : 'None yet'}</span>
            </div>
          </div>
        </section>

        <section className="wifi-settings-section">
          <div className="wifi-settings-grid">
            <div className="wifi-settings-card">
              <h4>Portal route</h4>
              <p className="wifi-settings-help">Use this route when you want to open the live captive portal directly for the currently mapped UniFi site.</p>
              {portalRoute ? (
                <>
                  <div className="wifi-settings-diagnosticRoute">{portalRoute}</div>
                  <p className="wifi-settings-field-help">Current UniFi site mapping: {mapping.unifiSite}</p>
                </>
              ) : (
                <p className="wifi-settings-muted">No UniFi site mapping saved yet.</p>
              )}
            </div>

            <div className="wifi-settings-card">
              <h4>What gets logged</h4>
              <ul className="wifi-settings-placeholder-list">
                <li>fresh guest authorizations</li>
                <li>automatic reconnects for recognized devices</li>
                <li>fingerprint-based recognition hits</li>
                <li>operator disconnect requests and results</li>
                <li>deauthorization failures and validation warnings</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="wifi-settings-section">
          <div className="wifi-settings-card">
            <div className="wifi-settings-section-header">
              <div>
                <h4>Live Wi-Fi event log</h4>
                <p className="wifi-settings-help">Newest events are shown first. Refresh manually when you want to pull the latest test activity.</p>
              </div>
              <div className="wifi-settings-actions">
                <button onClick={loadWifiEvents} disabled={!siteId || loadingWifiEvents}>
                  Refresh
                </button>
              </div>
            </div>

            {wifiEventsError && <p className="wifi-settings-error">{wifiEventsError}</p>}
            {loadingWifiEvents && <p className="wifi-settings-muted">Loading Wi-Fi diagnostics…</p>}

            <div className="wifi-settings-toolbar">
              <label className="wifi-settings-toolbar-field">
                <span>Search log</span>
                <input
                  value={diagnosticsSearch}
                  onChange={(e) => setDiagnosticsSearch(e.target.value)}
                  placeholder="Event, message, MAC, nickname"
                  disabled={!siteId}
                />
              </label>
            </div>

            {!loadingWifiEvents && filteredWifiEvents.length === 0 && (
              <p className="wifi-settings-muted">No Wi-Fi diagnostic events matched the current filter.</p>
            )}

            {filteredWifiEvents.length > 0 && (
              <div className="wifi-settings-logList">
                {filteredWifiEvents.map((event) => (
                  <article key={event.id} className="wifi-settings-logItem">
                    <div className="wifi-settings-logHeader">
                      <div className="wifi-settings-logMeta">
                        <span className={`wifi-settings-pill wifi-settings-pill-${event.severity || 'info'}`}>
                          {event.severity || 'info'}
                        </span>
                        <strong>{event.eventType}</strong>
                        <span className="wifi-settings-muted">{formatDateTime(event.createdAt)}</span>
                      </div>
                      <div className="wifi-settings-muted">{event.actorSource || 'system'}</div>
                    </div>
                    <div className="wifi-settings-logMessage">{event.message}</div>
                    <div className="wifi-settings-logContext">
                      <span>MAC: {event.clientMacMasked || event.clientMac || 'Unknown'}</span>
                      <span>Nickname: {event.nickname || '—'}</span>
                      <span>Session: {event.sessionId || '—'}</span>
                    </div>
                    {event.details && (
                      <pre className="wifi-settings-logDetails">{JSON.stringify(event.details, null, 2)}</pre>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </>
    );
  };

  return (
    <div className="wifi-settings">
      <div className="wifi-settings-topbar">
        <div>
          <h3>Wi‑Fi Settings</h3>
          <p className="wifi-settings-help">
            {hasSavedWifiController
              ? 'Configure captive Wi-Fi, then manage live connections, policy, portal content, and diagnostics.'
              : 'Finish the UniFi controller and site mapping first. The other Wi-Fi tabs stay hidden until setup is saved.'}
          </p>
        </div>
      </div>

      {!siteId && <p>Select a site to configure Wi‑Fi.</p>}

      {siteId && !hasSavedWifiController && (
        <p className="wifi-settings-help">
          Save a UniFi controller mapping in `Setup` to unlock `Connections`, `Access Policy`, `Portal Designer`, and `Diagnostics`.
        </p>
      )}

      <div className="wifi-settings-tabs" role="tablist" aria-label="Wi-Fi settings sections">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`wifi-settings-tab ${activeTab === tab.key ? 'wifi-settings-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'setup' &&
        renderSetupTab()}

      {activeTab === 'connections' &&
        renderConnectionsTab()}

      {activeTab === 'access' &&
        renderAccessPolicyTab()}

      {activeTab === 'designer' &&
        renderDesignerTab()}

      {activeTab === 'diagnostics' &&
        renderDiagnosticsTab()}
    </div>
  );
}

export default WifiSettings;
